





















import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/AdminCategory.css';
import axios from 'axios';

const AdminCategory = () => {
  const navigate = useNavigate();
  
  // State for categories
  const [categories, setCategories] = useState([]);
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  
  // State for form visibility
  const [showForm, setShowForm] = useState(false);
  
  // State for editing
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Show 3 items per page
  
  // API URL
  const apiUrl = 'https://ecl.net.in/backende';

  // Fetch categories on component mount
  useEffect(() => {
    // Check if user is admin
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin !== 'true') {
      navigate('/login');
      return;
    }
    
    fetchCategories();
  }, [navigate]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/equipment-categories`);
      
      // Handle different possible response formats
      const categoriesData = response.data.categories || response.data;
      
      // Ensure we're setting an array
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setErrorMessage('Failed to load categories. Please try again later.');
      setCategories([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare form data
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      
      // Only append description if it exists
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }

      if (editMode) {
        // Update existing category
        const response = await axios.put(
          `${apiUrl}/api/equipment-categories/${editId}`, 
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.success) {
          setSuccessMessage('Category updated successfully!');
          fetchCategories();
        } else {
          setErrorMessage(response.data.message || 'Failed to update category');
        }
      } else {
        // Add new category
        const response = await axios.post(
          `${apiUrl}/api/equipment-categories`, 
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response.data.success) {
          setSuccessMessage('Category added successfully!');
          fetchCategories();
        } else {
          setErrorMessage(response.data.message || 'Failed to add category');
        }
      }
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
      
      // More detailed error logging
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        setErrorMessage(`Failed to save category: ${error.response.data.detail || error.message}`);
      } else {
        setErrorMessage('Failed to save category. Please try again.');
      }
    }
  };

  // Handle edit button click
  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      description: category.description || ''
    });
    setEditId(category.id);
    setEditMode(true);
    setShowForm(true);
  };

  // Handle delete button click
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      '4 equipment items are using this category. Do you want to force delete and set their category to NULL?'
    );
    
    if (confirmDelete) {
      try {
        const response = await axios.delete(
          `${apiUrl}/api/equipment-categories/${id}`,
          { params: { force: true } }
        );
        
        if (response.data.success) {
          setSuccessMessage('Category deleted successfully!');
          fetchCategories(); // Refresh the list
        } else {
          setErrorMessage(response.data.message || 'Failed to delete category');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        setErrorMessage('Failed to delete category: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: ''
    });
    setEditId(null);
    setEditMode(false);
    setShowForm(false);
  };

  // Filter categories based on search term
  const filteredCategories = Array.isArray(categories) 
    ? categories.filter(category => {
        if (!searchTerm) return true;
        return category.name.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : [];
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  
  // Get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  
  // Change page
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Previous page
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Next page
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
    if (showForm) {
      resetForm();
    }
  };

  return (
    <div className="admin-category">
      <div className="admin-page-header">
        <h1>Equipment Categories</h1>
        <p>Manage equipment categories and classifications</p>
      </div>

      {/* Success and Error Messages */}
      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {/* Category Form */}
      {showForm && (
        <div className="category-form-container">
          <h2>{editMode ? 'Edit Category' : 'Add New Category'}</h2>
          
          <form onSubmit={handleSubmit} className="category-form">
            <div className="form-group">
              <label htmlFor="name">Category Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter category name (e.g. CRANES)"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter category description"
                rows="3"
              />
            </div>
            
            <div className="form-buttons">
              <button type="submit" className="save-category-btn">
                {editMode ? 'Update Category' : 'Save Category'}
              </button>
              <button type="button" className="cancel-btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Category Button and Search */}
      <div className="admin-controls">
        <button className="add-category-btn" onClick={toggleForm}>
          + Add New Category
        </button>
        
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Categories Table */}
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Category Name</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map(category => (
            <tr key={category.id}>
              <td>{category.id}</td>
              <td>{category.name}</td>
              <td>{category.description || '-'}</td>
              <td className="action-buttons">
                <button className="edit-btn" onClick={() => handleEdit(category)}>
                  Edit
                </button>
                <button className="delete-btn" onClick={() => handleDelete(category.id)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {currentItems.length === 0 && (
            <tr>
              <td colSpan="4" className="no-results">No categories found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {filteredCategories.length > 0 && (
        <div className="pagination">
          <button 
            onClick={handlePreviousPage} 
            disabled={currentPage === 1}
          >
            &lt; Previous
          </button>
          <div className="page-numbers">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                className={currentPage === index + 1 ? 'active' : ''}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button 
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next &gt;
          </button>
        </div>
      )}

      {/* Back Link */}
      {/* <div className="back-link">
        <Link to="/admin/master-equipment">← Back to Master Equipment</Link>
      </div> */}
    </div>
  );
};

export default AdminCategory;